<template>
  <PopupBackground @click="$emit('close')">
    <div class="duplicate-console-popup popup-shadow">
      <!-- Header -->
      <div class="header">
        <div class="title">
          <h3>Share {{ dashboard_name }}</h3>
        </div>
      </div>

      <div class="contents">
        <label
          >Private Link (People with link in this organization will be able to
          view and edit)</label
        >
        <div class="copy-holder">
          <icon class="link-icon">lock</icon>
          <input
            type="text"
            v-model="getDahboardLink"
            placeholder="Enter a console name"
            ref="inputName"
            class="ellipsis"
            readonly
          />
          <icon class="copy-button" type="button" @click="copyDashboardLink"
            >file_copy</icon
          >
        </div>
        <div v-if="allowPublicMode && getPublicLink" class="flex-col">
          <label>Public Link (Anyone with the link can view)</label>
          <div class="copy-holder">
            <icon class="link-icon">language</icon>
            <input
              type="text"
              v-model="getPublicLink"
              placeholder="Enter the console description"
              ref="inputDescription"
              readonly
              class="ellipsis"
            />
            <icon class="copy-button" type="button" @click="copyPublicLink"
              >file_copy</icon
            >
          </div>
          <p
            v-if="!isRequestInProgress"
            class="disable-share-link"
            @click="removePublicLink"
          >
            <icon size="18">clear</icon> Disable public access
          </p>
        </div>
        <div
          v-else-if="allowPublicMode && !getPublicLink"
          v-show="!isRequestInProgress"
        >
          <p class="create-share-link" @click="generatePublicLink">
            <icon size="18">language</icon> Create public link
          </p>
        </div>
        <div v-if="isRequestInProgress">
          <spinner small />
        </div>
      </div>
      <!-- Footer -->
      <div class="footer">
        <expand />
        <Button
          type="primary"
          text="Done"
          icon="check"
          @click="$emit('close')"
        />
      </div>
    </div>
  </PopupBackground>
</template>

<script>
import PopupBackground from "@/components/modals/PopupBackground";
import Button from "@/components/global/views/Button";
import { mapActions, mapGetters } from "vuex";
import notify from "@/lib/notifications";

export default {
  components: {
    PopupBackground,
    Button,
  },
  props: {
    dashboard_id: Number,
    dashboard_name: String,
    magic_link: String,
    target_type: String,
    internal_path: String,
    // For hiding public mode shareability when passed false prop:
    allowPublicMode: { type: Boolean, default: true },
  },
  data() {
    return {
      isRequestInProgress: false,
      newConsolename: "",
      newDescription: "",
      publicLink: null,
    };
  },
  computed: {
    getDahboardLink() {
      if (this.internal_path)
        return `${window.location.origin}${this.internal_path}`;
      else return `${window.location.origin}${this.$route.fullPath}`;
    },
    getPublicLink() {
      return this.publicLink
        ? `${window.location.origin}/${this.target_type}/public/${this.publicLink}`
        : null;
    },
  },
  methods: {
    ...mapActions("Dashboards", ["createPublicLink", "disablePublicLink"]),
    async generatePublicLink() {
      this.isRequestInProgress = true;
      try {
        const { dashboard_id, public_url } = await this.createPublicLink({
          dashboard_id: this.dashboard_id,
        });

        if (!public_url) {
          throw new Error("Unable to retrieve public link");
        }

        this.publicLink = public_url;
        this.$emit("createLink", public_url);
        this.isRequestInProgress = false;
      } catch (error) {
        console.error(error);
        this.isRequestInProgress = false;
      }
    },
    async removePublicLink() {
      this.isRequestInProgress = true;
      try {
        const { dashboard_id } = await this.disablePublicLink({
          dashboard_id: this.dashboard_id,
        });
        this.publicLink = null;
        this.$emit("removeLink");
        this.isRequestInProgress = false;
      } catch (error) {
        console.error(error);
        this.isRequestInProgress = false;
      }
    },
    copyDashboardLink() {
      this.$clipboard(this.getDahboardLink);
      notify.success("Link copied to clipboard");
    },
    copyPublicLink() {
      this.$clipboard(this.getPublicLink);
      notify.success("Link copied to clipboard");
    },
  },
  mounted() {
    this.publicLink = this.magic_link;
  },
};
</script>

<style lang="scss" scoped>
.duplicate-console-popup {
  display: flex;
  flex-direction: column;
  background: var(--color-secondary);
  border-radius: 5px;
  width: 80%;
  max-width: 450px;

  .header,
  .footer,
  .contents {
    padding: 10px;
  }

  .header {
    padding: 20px 15px 10px 15px;
    display: flex;
    align-items: center;

    .title {
      display: flex;
      flex-direction: column;

      h3 {
        font-weight: bold;
      }

      p {
        margin: 0;
        opacity: 0.8;
        padding: 10px 0 0 0;
      }
    }
  }

  .contents {
    display: flex;
    flex-direction: column;
    padding: 15px;

    label {
      margin: 0 0 5px 0;
    }
    input {
      margin: 0 0 0 0;
      flex-grow: 1;
    }

    .copy-holder {
      display: flex;
      position: relative;
      flex-direction: row;
      align-items: center;
      margin-bottom: 15px;

      .link-icon {
        padding-right: 10px;
      }

      .copy-button {
        position: absolute;
        right: 0;
      }

      input {
        padding-right: 50px;
      }
    }

    .disable-share-link {
      display: flex;
      flex-direction: row;
      align-items: center;
      color: var(--color-primary);
      margin: 0;
      transition: 0.3s all ease-in-out;
      text-align: end;

      i {
        margin-right: 5px;
      }

      &:hover {
        cursor: pointer;
        opacity: 0.7;
      }
    }

    .create-share-link {
      display: flex;
      flex-direction: row;
      align-items: center;
      color: var(--color-primary);
      margin: 0;
      transition: 0.3s all ease-in-out;

      i {
        margin-right: 5px;
      }

      &:hover {
        cursor: pointer;
        opacity: 0.7;
      }
    }
  }

  .footer {
    background-color: var(--color-default-background);
    display: flex;
    align-items: center;
    border-radius: 0 0 5px 5px;
  }
}
</style>
``
